'use client';

import type { BoxProps } from '@mui/material';

import { useMemo, useCallback } from 'react';

import { Box } from '@mui/material';

import { useAuthContext } from 'src/auth/hooks';

import { useDemoPopover } from './demo-popover-provider';

// ----------------------------------------------------------------------

export interface DemoActionGuard {
  wrap: <T extends (...args: any[]) => any = (...args: any[]) => any>(
    fn?: T,
    reason?: string
  ) => (...args: Parameters<T>) => ReturnType<T> | void;
  wrapWithPopover: <T extends (...args: any[]) => any = (...args: any[]) => any>(
    fn?: T,
    reason?: string
  ) => (...args: Parameters<T>) => ReturnType<T> | void;
  wrapWithSnackbar: <T extends (...args: any[]) => any = (...args: any[]) => any>(
    fn?: T,
    reason?: string
  ) => (...args: Parameters<T>) => ReturnType<T> | void;
  isLocked: (index: number, limit?: number) => boolean;
  isLockedFirst: (index: number, count?: number) => boolean;
  onLocked: (reason?: string) => () => void;
  isDemo: boolean;
}

/**
 * 데모 플랜에서 「누르면 안 되는 것」을 감싸 주는 가드 모음.
 *
 * ⚠️ 반환 타입(`DemoActionGuard`)을 지워서는 안 된다 — 없으면 `.ts`/`.tsx` 호출부가
 *    `isDemo` 를 `unknown` 으로 받아 컴파일에 걸린다. `isDemo` 는 `planId === plan_demo`
 *    비교라 boolean 이다.
 */
export function useDemoActionGuard(): DemoActionGuard {
  const { isDemo } = useAuthContext();
  const { openSnackbar, openPopover } = useDemoPopover();

  // 버튼 / pagination 클릭 차단 → 스낵바
  const wrap = useCallback(
    <T extends (...args: any[]) => any = (...args: any[]) => any>(
      fn?: T,
      reason?: string
    ) =>
      (...args: Parameters<T>): ReturnType<T> | void =>
        isDemo ? (openSnackbar(reason) as void) : fn?.(...args),
    [isDemo, openSnackbar]
  );

  // 버튼 클릭 차단 → 팝오버 (플랜 페이지 등에서 사용)
  const wrapWithPopover = useCallback(
    <T extends (...args: any[]) => any = (...args: any[]) => any>(
      fn?: T,
      reason?: string
    ) =>
      (...args: Parameters<T>): ReturnType<T> | void =>
        isDemo ? (openPopover(reason) as void) : fn?.(...args),
    [isDemo, openPopover]
  );

  // 버튼 클릭 차단 → 스낵바
  const wrapWithSnackbar = useCallback(
    <T extends (...args: any[]) => any = (...args: any[]) => any>(
      fn?: T,
      reason?: string
    ) =>
      (...args: Parameters<T>): ReturnType<T> | void =>
        isDemo ? (openSnackbar(reason) as void) : fn?.(...args),
    [isDemo, openSnackbar]
  );

  // 리스트 blur: index(또는 1-based 순위) >= limit 이면 잠금. 예: isLocked(rowRank, 4) → 4위부터 블러
  const isLocked = useCallback(
    (index: number, limit = 10): boolean => Boolean(isDemo && index >= limit),
    [isDemo]
  );

  // 리스트 blur (앞에서 count개 항목 잠금, 예: 1~10위)
  const isLockedFirst = useCallback(
    (index: number, count = 10): boolean => Boolean(isDemo && index < count),
    [isDemo]
  );

  // blur 클릭 시 스낵바만 (팝오버 제외)
  const onLocked = useCallback(
    (reason?: string) => () => {
      if (isDemo) openSnackbar(reason);
    },
    [isDemo, openSnackbar]
  );

  return useMemo(
    () => ({
      wrap,
      wrapWithPopover,
      wrapWithSnackbar,
      isLocked,
      isLockedFirst,
      onLocked,
      isDemo: Boolean(isDemo),
    }),
    [wrap, wrapWithPopover, wrapWithSnackbar, isLocked, isLockedFirst, onLocked, isDemo]
  );
}

// ----------------------------------------------------------------------
// DOM 레벨 클릭 차단: 데모일 때 자식은 비활성화, 클릭 시 스낵바만 (팝오버 제외)
// ----------------------------------------------------------------------

export type DemoGuardAreaProps = BoxProps & {
  reason?: string;
  children: React.ReactNode;
};

export function DemoGuardArea({
  reason = '데모에서는 사용할 수 없어요',
  children,
  sx,
  ...rest
}: DemoGuardAreaProps) {
  const { isDemo } = useAuthContext();
  const { openSnackbar } = useDemoPopover();

  if (!isDemo) {
    return <>{children}</>;
  }

  const handleOverlayClick = () => {
    openSnackbar(reason);
  };

  return (
    <Box sx={{ position: 'relative', ...sx }} {...rest}>
      <Box sx={{ pointerEvents: 'none', opacity: 0.85 }}>{children}</Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          cursor: 'not-allowed',
        }}
        role="button"
        tabIndex={0}
        aria-label="데모 제한"
        onClick={handleOverlayClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOverlayClick();
          }
        }}
      />
    </Box>
  );
}